/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { ReactElement, useCallback, useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import LiveLoading from 'live/components/ui/Loading'
import i18n from 'live/services/i18n'
import authorization from 'live/services/authorization'
import { CustomModalsProps, GenericWarning, ModalTabSections } from '../modal-components'
import { ModalHeader, ModalBody, ModalFooter, ModalButton } from '../modal-components-style'
import { RawInterventionEvent, RawWellEvent, SelectOptions } from '../../utils/type'
import { dateFormat, timeFormat } from '../../utils/parse'
import {
    createSingleEntity,
    fetchEntityEndTimestamp,
    fetchWells,
    INTERVENTION_API,
    SECTION_API,
    updateSingleEntity,
    WELL_API
} from '../../api/api'
import { StaticDataType, STATIC_DATA_STATES } from '../chart-components'
import InterventionTab from './InterventionTab'
import { emptyErrors, emptyInterventionErrors, emptyWellErrors, Errors, InterventionErrors, WellErrors } from './types'
import WellTab from './WellTab'
import { StaticDataContext, useStaticDataWidget } from '../../context/StaticDataContext'
import { useRequest } from 'ahooks'
import { deleteWellNoIntervention } from '../../api/wellApi'

const errorMessages = {
    requiredField: 'Required field',
    dateOutFormat: `date out of format ${dateFormat}`,
    timeOutFormat: `time out of format ${timeFormat}`,
    inputNumber: `only numeric value`,
    intervention: `Select the intervention tab and fill in the intervention attributes.`,
    well: 'Select the well tab and fill in the well attributes.',
    modalError: `Required field.`
}
interface InterventionModalProps extends CustomModalsProps {
    event: RawInterventionEvent
    wellEvent: RawWellEvent
}

const emptyRawInterventionEvent: RawInterventionEvent = {
    id: null,
    name: null,
    tatic: null,
    responsible: null,
    start: null,
    end: null,
    well_id: null,
    rig_id: null,
    state: STATIC_DATA_STATES.EMPTY
}
const emptyRawWellEvent: RawWellEvent = {
    id: null,
    name: '',
    wellCategory: null,
    fieldName: null,
    latitude: null,
    longitude: null,
    referenceSystem: null,
    wellType: null,
    waterDepth: { uom: null, value: 0 },
    state: STATIC_DATA_STATES.EMPTY
}
const InterventionModal = ({
    title = StaticDataType.INTERVENTION,
    closeModal,
    event,
    wellEvent
}: InterventionModalProps): ReactElement => {
    const { reloadData } = useStaticDataWidget()
    const [interventionRawEvent, setInterventionRawEvent] = useState<RawInterventionEvent>(
        event?.id ? event : { ...emptyRawInterventionEvent, ...event, end: null }
    )
    const [loading, setLoading] = useState<boolean>(true)
    const [wellRawEvent, setWellRawEvent] = useState<RawWellEvent>(wellEvent || emptyRawWellEvent)
    const updateInterventionEvent = useCallback((event: RawInterventionEvent) => setInterventionRawEvent(event), [])
    const updateWellRawEvent = useCallback((event: RawWellEvent) => setWellRawEvent(event), [])
    const [errors, setErrors] = useState<Errors>(emptyErrors())
    const [selectedTab, setSelectedTab] = useState<'well' | 'intervention'>('well')
    const { liveTheme } = useContext(StaticDataContext)

    const hasPermissionToEdit =
        authorization.superAdmin ||
        authorization.admin ||
        authorization.Permission.ADMIN ||
        authorization.Permission.OGPERFORMANCE_MANAGE_KPI ||
        authorization.Permission.OGPERFORMANCE_ADMINISTRATE_KPI

    const { data: wellNameOptions } = useRequest<SelectOptions[], any>(() =>
        fetchWells().then(ws => ws.map(w => ({ value: w.id, label: w.name })))
    )

    const postWell = (rawWell: RawWellEvent): Promise<any> => {
        return (rawWell.id ? updateSingleEntity : createSingleEntity)(WELL_API, rawWell)
    }
    const postIntervention = (rawIntervention: RawInterventionEvent): any => {
        const interventionData = (({ state, begin, ...rawIntervention }) => rawIntervention)(rawIntervention)
        return (rawIntervention.id ? updateSingleEntity : createSingleEntity)(INTERVENTION_API, interventionData)
    }

    function validateFields(
        interventionEvent: RawInterventionEvent,
        wellEvent: RawWellEvent
    ): { pass: boolean; errors: any } {
        const errors: Errors = emptyErrors()
        const { pass: passWell, errors: wellErrors } = validateWellFields(wellEvent)
        const { pass: passIntervention, errors: interventionErrors } = validateInterventionFields(interventionEvent)
        if (!passWell) {
            errors.messages.well = errorMessages.well
            errors.well = wellErrors
        }
        if (!passIntervention) {
            errors.messages.intervention = errorMessages.intervention
            errors.intervention = interventionErrors
        }

        return {
            pass: passWell && passIntervention,
            errors: errors
        }
    }

    const validateInterventionFields = useCallback((event: RawInterventionEvent): {
        pass: boolean
        errors: InterventionErrors
    } => {
        // name
        let pass = true
        let errors: InterventionErrors = emptyInterventionErrors


        if (!event.name || event.name.length <= 0) {
            pass = false
            errors = { ...errors, name: true }
        }

        // start date
        if (!event.start) {
            pass = false
            errors = { ...errors, start: true }
        }

        return { pass, errors }
    }, [])

    const validateWellFields = useCallback(
        (event: RawWellEvent): { pass: boolean; errors: WellErrors } => {
            let pass = true
            let errors: WellErrors = emptyWellErrors

            // well name related to well asset of live
            if (!event.assetId || !event.name || !wellNameOptions?.some(op => op.label === event.name)) {
                pass = false
                errors = { ...errors, name: true }
            }

            // wellType
            if (!event.wellType || event.wellType === '') {
                pass = false
                errors = { ...errors, wellType: true }
            }

            // waterDepth
            if (event.wellType === 'OFFSHORE' && (!event.waterDepth?.value || !event.waterDepth?.uom)) {
                pass = false
                errors = { ...errors, waterDepth: true }
            }

            return { pass, errors }
        },
        [wellNameOptions]
    )

    useEffect(() => {
        async function fetchEventSpan() {
            return event.id
                ? fetchEntityEndTimestamp(event.id, INTERVENTION_API, SECTION_API).then(data => {
                      if (data) {
                          setInterventionRawEvent(prevstate => ({ ...prevstate, end: data }))
                      }
                  })
                : Promise.resolve()
        }
        fetchEventSpan().finally(() => setLoading(false))
    }, [event])

    const btn =
        event.state != STATIC_DATA_STATES.EMPTY
            ? event.state !== STATIC_DATA_STATES.FINISHED
                ? 'Finish'
                : 'Save'
            : 'Start'

    return (
        <>
            {loading ? (
                <LiveLoading />
            ) : (
                <InterventionModalContainer liveTheme={liveTheme}>
                    <ModalHeader>{i18n(title).toUpperCase()}</ModalHeader>
                    <header className="intervention-tabs-header">
                        <div
                            className={`intervention-tab ${selectedTab === 'well' ? 'active' : 'inactive'}`}
                            onClick={() => setSelectedTab('well')}
                        >
                            <div>{i18n('Well')}</div>
                        </div>
                        <div
                            className={`intervention-tab ${selectedTab === 'intervention' ? 'active' : 'inactive'}`}
                            onClick={() => setSelectedTab('intervention')}
                        >
                            <div>{i18n('Intervention')}</div>
                        </div>
                    </header>
                    <ModalBody liveTheme={liveTheme}>
                        <ModalTabSections
                            selected={selectedTab === 'intervention'}
                            content={
                                <InterventionTab
                                    event={interventionRawEvent}
                                    errors={errors?.intervention}
                                    updateInterventionEvent={updateInterventionEvent}
                                    hasPermissionToEdit={hasPermissionToEdit}
                                />
                            }
                        />
                        <ModalTabSections
                            selected={selectedTab === 'well'}
                            content={
                                <WellTab
                                    errors={errors?.well}
                                    updateWellEvent={updateWellRawEvent}
                                    event={wellRawEvent}
                                    wellNameOptions={wellNameOptions}
                                    hasPermissionToEdit={hasPermissionToEdit}
                                />
                            }
                        />
                        {selectedTab === 'intervention' && (
                            <>
                                {errors?.messages?.intervention && (
                                    <GenericWarning message={errorMessages.requiredField} />
                                )}
                                <GenericWarning message={errors?.messages?.well} />
                            </>
                        )}
                        {selectedTab === 'well' && (
                            <>
                                {errors?.messages?.well && <GenericWarning message={errorMessages.requiredField} />}
                                <GenericWarning message={errors?.messages?.intervention} />
                            </>
                        )}
                    </ModalBody>
                    <ModalFooter liveTheme={liveTheme}>
                        {hasPermissionToEdit && (
                            <ModalButton
                                styleType="action"
                                onClick={() => {
                                    const validation = validateFields(interventionRawEvent, wellRawEvent)
                                    if (validation.pass) {
                                        setErrors(emptyErrors())
                                        postWell(wellRawEvent).then(createdWell => {
                                            const previousWellId = interventionRawEvent?.well_id
                                            postIntervention({ ...interventionRawEvent, well_id: createdWell.id }).then(
                                                () => {
                                                    deleteWellNoIntervention(previousWellId)
                                                    closeModal()
                                                    reloadData()
                                                }
                                            )
                                        })
                                    } else {
                                        setErrors(validation.errors)
                                    }
                                }}
                                id="button-start"
                            >
                                {i18n(btn)}
                            </ModalButton>
                        )}
                        <ModalButton
                            styleType="cancel"
                            onClick={() => {
                                closeModal()
                            }}
                        >
                            {hasPermissionToEdit ? i18n('Cancel') : i18n('Close')}
                        </ModalButton>
                    </ModalFooter>
                </InterventionModalContainer>
            )}
        </>
    )
}

export default InterventionModal

const InterventionModalContainer = styled.div<{ liveTheme: string }>`
    height: 100%;
    display: flex;
    flex-flow: column;
    justify-content: space-between;
    .intervention-tabs-header {
        display: flex;
        justify-content: space-between;
        gap: 10px;

        background-color: ${({ liveTheme }) => (liveTheme === 'dark' ? 'var(--gray-200)' : 'white')};

        .intervention-tab {
            width: 100%;
            display: flex;
            justify-content: center;
            align-items: center;
            padding: 15px 0px;
            cursor: pointer;
            margin-left: 15px;
            margin-right: 15px;

            div {
                font-size: 14px;
                font-weight: 600;
                text-transform: uppercase;
            }

            &.active {
                border-bottom: 2px solid var(--blue-500);
                color: var(--gray-800);
            }

            &.inactive div {
                color: var(--blue-500);
            }
        }
    }

    .position-divider {
        border-top: 1px solid #dddee1;
        padding-top: 1.6rem;
        margin-bottom: 1.6rem;
        text-transform: uppercase;
        font-size: 13px;
    }

    .react-datepicker {
        position: fixed;
        width: 198px;
        .react-datepicker__header {
            width: 198px;
            .react-datepicker__day-name {
                width: 198px;
            }
            .react-datepicker__navigation {
                width: 198px;
            }
        }
    }

    .react-datepicker__month-container {
        width: 100%;
    }
    .react-datepicker__month {
        width: 100%;
        margin: 0 !important;
    }
`

const Line = styled.div`
    border-bottom: 1px solid #dddee1;
`
